import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Menu,
    X,
    Sun,
    Moon,
    ChevronRight,
    Heart,
    Download,
} from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
    DEFAULT_PRIMARY_NAV,
    SECONDARY_NAV_ITEMS,
    isRouteActive,
    type PrimaryNavItem,
} from "./mobile-nav-config";

interface MobileBottomNavProps {
    isMoreOpen: boolean;
    onOpenMore: () => void;
    onCloseMore: () => void;
    theme: "light" | "dark";
    onToggleTheme: () => void;
    language: "en" | "mi";
    onToggleLanguage: () => void;
    primaryItems?: PrimaryNavItem[];
    canInstall?: boolean;
    onInstallClick?: () => void;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
    isMoreOpen,
    onOpenMore,
    onCloseMore,
    theme,
    onToggleTheme,
    language,
    onToggleLanguage,
    primaryItems = DEFAULT_PRIMARY_NAV,
    canInstall = false,
    onInstallClick,
}) => {
    const { t } = useTranslation();
    const location = useLocation();
    const shouldReduceMotion = useReducedMotion();

    const moreButtonRef = useRef<HTMLButtonElement | null>(null);
    const sheetRef = useRef<HTMLDivElement | null>(null);
    const closeButtonRef = useRef<HTMLButtonElement | null>(null);

    // Focus management & Escape key trap for "More" bottom sheet
    useEffect(() => {
        if (!isMoreOpen) return;

        // Focus the close button when opened
        const timer = setTimeout(() => {
            closeButtonRef.current?.focus();
        }, 50);

        // Lock background body scroll
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onCloseMore();
                moreButtonRef.current?.focus();
                return;
            }

            // Trap focus within sheet
            if (e.key === "Tab" && sheetRef.current) {
                const focusables = sheetRef.current.querySelectorAll<HTMLElement>(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];

                if (e.shiftKey) {
                    if (document.activeElement === first) {
                        e.preventDefault();
                        last.focus();
                    }
                } else {
                    if (document.activeElement === last) {
                        e.preventDefault();
                        first.focus();
                    }
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            clearTimeout(timer);
            window.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = originalOverflow;
        };
    }, [isMoreOpen, onCloseMore]);

    // Check if any secondary route is currently active
    const isAnySecondaryActive = SECONDARY_NAV_ITEMS.some((item) =>
        isRouteActive(location.pathname, item.path)
    );

    return (
        <>
            {/* Mobile-only Bottom Dock (< 768px / md:hidden) */}
            <nav
                aria-label={t("nav.mobileNav")}
                className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-warm-dark-surface/95 backdrop-blur-md border-t border-warm-border/80 dark:border-warm-dark-border shadow-[0_-4px_16px_rgba(0,0,0,0.06)] pb-[max(0.35rem,env(safe-area-inset-bottom,0px))]"
            >
                <div className="grid grid-cols-5 items-stretch h-[3.85rem] px-1">
                    {/* 4 Primary Navigation Slots */}
                    {primaryItems.map((item) => {
                        const Icon = item.icon;
                        const active = isRouteActive(location.pathname, item.path);

                        return (
                            <Link
                                key={item.id}
                                to={item.path}
                                onClick={onCloseMore}
                                aria-current={active ? "page" : undefined}
                                className={`flex flex-col items-center justify-center min-h-[48px] px-1 py-1 rounded-xl transition-all duration-150 ${
                                    active
                                        ? "text-primary dark:text-green-400 font-extrabold"
                                        : "text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary active:scale-95"
                                }`}
                            >
                                <div className="relative">
                                    <Icon size={20} aria-hidden={true} className="shrink-0" />
                                    {active && (
                                        <span
                                            className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-primary dark:bg-green-400"
                                            aria-hidden={true}
                                        />
                                    )}
                                </div>
                                <span className="text-[10px] font-bold tracking-tight mt-1 truncate max-w-full text-center leading-none uppercase">
                                    {t(item.labelKey)}
                                </span>
                            </Link>
                        );
                    })}

                    {/* 5th Slot: More / Hamburger Menu */}
                    <button
                        ref={moreButtonRef}
                        type="button"
                        onClick={() => (isMoreOpen ? onCloseMore() : onOpenMore())}
                        aria-expanded={isMoreOpen}
                        aria-haspopup="dialog"
                        aria-controls="mobile-more-sheet"
                        aria-label={isMoreOpen ? t("nav.closeMenu") : t("nav.openMenu")}
                        className={`flex flex-col items-center justify-center min-h-[48px] px-1 py-1 rounded-xl transition-all duration-150 ${
                            isMoreOpen || isAnySecondaryActive
                                ? "text-primary dark:text-green-400 font-extrabold"
                                : "text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary active:scale-95"
                        }`}
                    >
                        <div className="relative">
                            {isMoreOpen ? (
                                <X size={20} aria-hidden={true} className="shrink-0" />
                            ) : (
                                <Menu size={20} aria-hidden={true} className="shrink-0" />
                            )}
                            {isAnySecondaryActive && !isMoreOpen && (
                                <span
                                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-primary dark:bg-green-400"
                                    aria-hidden={true}
                                />
                            )}
                        </div>
                        <span className="text-[10px] font-bold tracking-tight mt-1 truncate max-w-full text-center leading-none uppercase">
                            {t("nav.more")}
                        </span>
                    </button>
                </div>
            </nav>

            {/* Accessible "More" Bottom Sheet Dialog */}
            <AnimatePresence>
                {isMoreOpen && (
                    <div className="md:hidden fixed inset-0 z-50 flex flex-col justify-end">
                        {/* Backdrop */}
                        <motion.div
                            initial={shouldReduceMotion ? false : { opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={shouldReduceMotion ? undefined : { opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            onClick={() => {
                                onCloseMore();
                                moreButtonRef.current?.focus();
                            }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-xs"
                            aria-hidden="true"
                        />

                        {/* Sheet Content */}
                        <motion.div
                            ref={sheetRef}
                            id="mobile-more-sheet"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="more-sheet-title"
                            initial={shouldReduceMotion ? false : { y: "100%" }}
                            animate={{ y: 0 }}
                            exit={shouldReduceMotion ? undefined : { y: "100%" }}
                            transition={{
                                type: "spring",
                                damping: 28,
                                stiffness: 300,
                            }}
                            className="relative z-10 w-full max-h-[85vh] bg-white dark:bg-warm-dark-surface rounded-t-3xl border-t border-warm-border dark:border-warm-dark-border shadow-2xl flex flex-col overflow-hidden pb-[max(1rem,env(safe-area-inset-bottom,0px))]"
                        >
                            {/* Grab handle for touch affordance */}
                            <div className="pt-3 pb-1 flex justify-center">
                                <span
                                    className="w-12 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600"
                                    aria-hidden="true"
                                />
                            </div>

                            {/* Header */}
                            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-800">
                                <div>
                                    <h2
                                        id="more-sheet-title"
                                        className="text-base font-bold text-secondary dark:text-gray-100"
                                    >
                                        {t("nav.menuTitle")}
                                    </h2>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {t("nav.allPages")}
                                    </p>
                                </div>
                                <button
                                    ref={closeButtonRef}
                                    type="button"
                                    onClick={() => {
                                        onCloseMore();
                                        moreButtonRef.current?.focus();
                                    }}
                                    className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
                                    aria-label={t("nav.closeMenu")}
                                >
                                    <X size={22} aria-hidden={true} />
                                </button>
                            </div>

                            {/* Scrollable Body */}
                            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 overscroll-contain">
                                {/* Secondary Navigation Links */}
                                <div className="space-y-1">
                                    {SECONDARY_NAV_ITEMS.map((item) => {
                                        const Icon = item.icon;
                                        const active = isRouteActive(location.pathname, item.path);

                                        return (
                                            <Link
                                                key={item.id}
                                                to={item.path}
                                                onClick={onCloseMore}
                                                aria-current={active ? "page" : undefined}
                                                className={`flex items-center justify-between min-h-[48px] px-3.5 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                                                    active
                                                        ? "text-primary bg-green-50 dark:bg-gray-800/80"
                                                        : "text-gray-700 dark:text-gray-200 hover:text-primary hover:bg-gray-50 dark:hover:bg-gray-800/60"
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className={`p-2 rounded-lg ${
                                                            active
                                                                ? "bg-primary/10 text-primary dark:text-green-400"
                                                                : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                                                        }`}
                                                    >
                                                        <Icon size={18} aria-hidden={true} />
                                                    </div>
                                                    <span>{t(item.labelKey)}</span>
                                                </div>
                                                <ChevronRight
                                                    size={16}
                                                    className="text-gray-400"
                                                    aria-hidden={true}
                                                />
                                            </Link>
                                        );
                                    })}
                                </div>

                                {/* Preferences & Language / Theme Controls */}
                                <div className="border-t border-gray-100 dark:border-gray-800 pt-3">
                                    <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 px-2 mb-2">
                                        {t("nav.settings")}
                                    </h3>
                                    <div className="grid grid-cols-2 gap-2">
                                        {/* Theme Toggle Button */}
                                        <button
                                            type="button"
                                            onClick={onToggleTheme}
                                            className="flex items-center justify-center gap-2 min-h-[48px] px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/60 text-xs font-bold text-gray-700 dark:text-gray-200 hover:border-primary transition-colors"
                                            aria-label={
                                                theme === "dark"
                                                    ? t("nav.switchToLight")
                                                    : t("nav.switchToDark")
                                            }
                                        >
                                            {theme === "dark" ? (
                                                <>
                                                    <Sun size={16} className="text-amber-500" aria-hidden={true} />
                                                    <span>{t("nav.switchToLight")}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Moon size={16} className="text-gray-600 dark:text-gray-400" aria-hidden={true} />
                                                    <span>{t("nav.switchToDark")}</span>
                                                </>
                                            )}
                                        </button>

                                        {/* Language Toggle Button */}
                                        <button
                                            type="button"
                                            onClick={onToggleLanguage}
                                            className="flex items-center justify-center gap-2 min-h-[48px] px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/60 text-xs font-bold text-gray-700 dark:text-gray-200 hover:border-primary transition-colors"
                                            aria-label={
                                                language === "en"
                                                    ? t("nav.switchToMaori")
                                                    : t("nav.switchToEnglish")
                                            }
                                        >
                                            <span className="font-extrabold text-primary">
                                                {language === "en" ? "MI" : "EN"}
                                            </span>
                                            <span>
                                                {language === "en"
                                                    ? t("nav.switchToMaori")
                                                    : t("nav.switchToEnglish")}
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                {/* Install App Option (shown if not already in standalone mode) */}
                                {canInstall && (
                                    <div className="pt-1">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                onCloseMore();
                                                onInstallClick?.();
                                            }}
                                            className="flex items-center justify-between w-full min-h-[48px] px-3.5 py-2.5 rounded-2xl text-xs font-bold bg-primary/10 hover:bg-primary/20 text-primary dark:text-green-400 border border-primary/20 transition-colors"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-primary/20 text-primary dark:text-green-400 shrink-0">
                                                    <Download size={18} aria-hidden={true} />
                                                </div>
                                                <div className="text-left">
                                                    <span className="block leading-tight text-sm font-bold text-secondary dark:text-gray-100">
                                                        {t("pwa.installApp")}
                                                    </span>
                                                    <span className="text-[11px] font-normal text-gray-500 dark:text-gray-400 block mt-0.5">
                                                        {t("pwa.installAppDesc")}
                                                    </span>
                                                </div>
                                            </div>
                                            <ChevronRight size={16} aria-hidden={true} className="text-primary/70 shrink-0" />
                                        </button>
                                    </div>
                                )}

                                {/* Give Online Quick Action Button */}
                                <div className="pt-1">
                                    <Link
                                        to="/giving"
                                        onClick={onCloseMore}
                                        className="flex items-center justify-center gap-2 w-full min-h-[48px] px-4 py-3 rounded-full text-sm font-bold text-white bg-primary hover:bg-primary-hover shadow-sm transition-all"
                                    >
                                        <Heart size={18} aria-hidden={true} className="fill-white/20" />
                                        <span>{t("nav.giveOnline")}</span>
                                    </Link>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </>
    );
};

export default MobileBottomNav;
